import { Handshake, LineChart, type LucideIcon } from "lucide-react";

import { Logo } from "@/components/cinch/Logo";

interface Pillar {
  icon: LucideIcon;
  title: string;
  body: string;
}

const PILLARS: Pillar[] = [
  {
    icon: Handshake,
    title: "Help businesses create better relationships with their customers",
    body: "Every product we build exists to make the relationship between a business and its customers easier, warmer, and more durable — turning one-time transactions into lasting loyalty.",
  },
  {
    icon: LineChart,
    title: "Close the data gap",
    body: "Businesses are drowning in disconnected data. We unify it, make sense of it, and put the right insight in front of the right person at the right moment.",
  },
];

export function MissionPage() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-10">
      <div className="flex justify-center">
        <Logo variant="wordmark" tone="brand" className="h-10 w-auto" />
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight">Cinch Mission</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          What we&apos;re here to do, and why it matters.
        </p>
      </div>

      {/* Our Mission — the two pillars */}
      <section className="flex flex-col gap-4">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Our Mission
        </h2>
        {PILLARS.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="flex items-start gap-4 rounded-xl bg-brand-xlight p-6"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-light">
              <Icon className="h-5 w-5 text-brand-deep" />
            </div>
            <div className="flex flex-col gap-1">
              <h3 className="font-semibold leading-snug text-foreground">{title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Where we're headed — the vision narrative */}
      <section className="flex flex-col gap-4">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Where we&apos;re headed
        </h2>

        <p className="border-l-2 border-brand pl-4 text-lg font-medium leading-relaxed text-foreground">
          We empower local business owners with enterprise-level customer
          intelligence so they can punch above their weight class while building
          genuine customer relationships through data and AI.
        </p>

        <div className="flex flex-col gap-4 text-[15px] leading-relaxed text-foreground/90">
          <p>
            For a long time, the ability to truly understand and act on customer
            data belonged only to the largest companies — the ones with the
            budgets, the data teams, and the infrastructure to unify everything
            they knew about a customer. That created a lopsided world where the
            biggest players got smarter while everyone else was left guessing. We
            don&apos;t want to live in that world. We&apos;re closing that gap by
            putting the same machine learning and AI tools the giants use into the
            hands of the businesses that make up the real, physical economy — so a
            shop with a handful of locations can compete on customer intelligence
            with a national chain.
          </p>
          <p>
            What ties our customers together isn&apos;t an industry, it&apos;s a
            shared reality: they operate in the physical world. Their customers
            live somewhere, drive somewhere, and show up in person. Relationships
            are built on repeat visits, local context, and scheduling. That&apos;s
            why geography and timing sit at the core of our platform — we connect
            where a customer lives to the store or service location they actually
            walk into, and help businesses reach them at the right moment.
            We&apos;re strongest in service-based verticals like automotive and
            home services, and we go deep across golf, fitness, landscaping,
            veterinary and animal care, restaurants, and retail — including
            omnichannel operators blending brick-and-mortar with ecommerce.
          </p>
          <p>
            But the point was never just to help businesses win. It&apos;s about
            how they win. Somewhere along the way, scale killed intimacy. You used
            to be able to walk into a business, talk to the owner, and know they
            actually cared about you. We&apos;re trying to bring that back — not
            replace it with generic automation, but use data and AI to scale it.
            When a business can remember its customers, understand what they need,
            and reach out with something that actually matters to them, everyone
            gets the personalized experience they deserve, and the business earns a
            relationship that lasts.
          </p>
        </div>
      </section>
    </div>
  );
}
